import { Todo } from "@/types/todo";
import {
  CopyResult,
  copyTaskToClipboard,
  generateCopySuccessDescription,
  generateCopySuccessMessage,
} from "@/utils/copyTaskToClipboard";
import { useCallback, useRef, useState } from "react";
import { toast } from "@/components/ui/sonner";

/**
 * Copy button state for visual feedback
 */
export interface CopyButtonState {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  buttonText: string;
  buttonIcon: "copy" | "check" | "x" | "loader";
  ariaLabel: string;
}

/**
 * Enhanced copy feedback hook following industry best practices
 *
 * Features:
 * - Visual button state changes (loading, success, error)
 * - Detailed toast notifications with task information
 * - Accessibility support with ARIA labels and live regions
 * - Automatic state reset after feedback period
 * - Non-intrusive design that doesn't disrupt workflow
 *
 * Following UX principles:
 * - Nielsen's Heuristic #1: Visibility of system status
 * - Immediate and clear feedback
 * - Consistent feedback mechanism
 * - Accessibility for all users
 */
export const useCopyFeedback = () => {
  const [buttonStates, setButtonStates] = useState<
    Record<string, CopyButtonState>
  >({});
  const timeoutRefs = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const ariaLiveRef = useRef<HTMLDivElement | null>(null);

  /**
   * Get current button state for a specific task
   */
  const getButtonState = useCallback(
    (taskId: string): CopyButtonState => {
      return (
        buttonStates[taskId] || {
          isLoading: false,
          isSuccess: false,
          isError: false,
          buttonText: "Copy",
          buttonIcon: "copy",
          ariaLabel: "Copy task to clipboard",
        }
      );
    },
    [buttonStates]
  );

  /**
   * Update button state for a specific task
   */
  const updateButtonState = useCallback(
    (taskId: string, state: Partial<CopyButtonState>) => {
      setButtonStates((prev) => ({
        ...prev,
        [taskId]: {
          ...getButtonState(taskId),
          ...state,
        },
      }));
    },
    [getButtonState]
  );

  /**
   * Clear existing timeout for a task
   */
  const clearTimeoutForTask = useCallback((taskId: string) => {
    if (timeoutRefs.current[taskId]) {
      clearTimeout(timeoutRefs.current[taskId]);
      delete timeoutRefs.current[taskId];
    }
  }, []);

  /**
   * Reset button state to default after delay
   */
  const resetButtonState = useCallback(
    (taskId: string, delay: number = 2000) => {
      clearTimeoutForTask(taskId);
      timeoutRefs.current[taskId] = setTimeout(() => {
        setButtonStates((prev) => {
          const updated = { ...prev };
          delete updated[taskId];
          return updated;
        });
      }, delay);
    },
    [clearTimeoutForTask]
  );

  /**
   * Update ARIA live region for screen readers
   */
  const updateAriaLive = useCallback((message: string) => {
    if (ariaLiveRef.current) {
      ariaLiveRef.current.textContent = message;
      // Clear after announcement
      setTimeout(() => {
        if (ariaLiveRef.current) {
          ariaLiveRef.current.textContent = "";
        }
      }, 1000);
    }
  }, []);

  /**
   * Enhanced copy operation with comprehensive feedback
   */
  const copyTaskWithFeedback = useCallback(
    async (todo: Todo): Promise<CopyResult> => {
      const taskId = todo.id;

      // Clear any existing timeout
      clearTimeoutForTask(taskId);

      // Set loading state
      updateButtonState(taskId, {
        isLoading: true,
        isSuccess: false,
        isError: false,
        buttonText: "Copying...",
        buttonIcon: "loader",
        ariaLabel: `Copying task "${todo.title}" to clipboard`,
      });

      try {
        // Perform the copy operation
        const result = await copyTaskToClipboard(todo);

        if (result.success) {
          // Generate success messages
          const successMessage = generateCopySuccessMessage(result);
          const successDescription = generateCopySuccessDescription(result);

          // Update button to success state
          updateButtonState(taskId, {
            isLoading: false,
            isSuccess: true,
            isError: false,
            buttonText: "Copied!",
            buttonIcon: "check",
            ariaLabel: `Successfully copied task "${todo.title}" to clipboard`,
          });

          // Show enhanced success toast
          toast.success(successMessage, {
            description: successDescription,
            duration: 3000,
            action: {
              label: "Preview",
              onClick: () => {
                // Show a preview of copied content
                const previewLength = 150;
                const preview =
                  result.formattedText.length > previewLength
                    ? result.formattedText.substring(0, previewLength) + "..."
                    : result.formattedText;

                toast.info("Copied Content Preview", {
                  description: preview,
                  duration: 8000,
                });
              },
            },
          });

          // Update ARIA live region
          updateAriaLive(
            `Task "${todo.title}" copied to clipboard successfully`
          );

          // Reset button state after success display
          resetButtonState(taskId, 2000);

          return result;
        } else {
          // Handle copy failure
          const errorMessage = result.error || "Copy operation failed";

          // Update button to error state
          updateButtonState(taskId, {
            isLoading: false,
            isSuccess: false,
            isError: true,
            buttonText: "Failed",
            buttonIcon: "x",
            ariaLabel: `Failed to copy task "${todo.title}" to clipboard`,
          });

          // Show error toast with helpful information
          toast.error("Copy Failed", {
            description: errorMessage,
            duration: 4000,
            action: {
              label: "Retry",
              onClick: () => copyTaskWithFeedback(todo),
            },
          });

          // Update ARIA live region
          updateAriaLive(
            `Failed to copy task "${todo.title}": ${errorMessage}`
          );

          // Reset button state after error display
          resetButtonState(taskId, 3000);

          return result;
        }
      } catch (error) {
        // Handle unexpected errors
        const errorMessage =
          error instanceof Error ? error.message : "Unexpected error occurred";

        // Update button to error state
        updateButtonState(taskId, {
          isLoading: false,
          isSuccess: false,
          isError: true,
          buttonText: "Error",
          buttonIcon: "x",
          ariaLabel: `Error copying task "${todo.title}" to clipboard`,
        });

        // Show error toast
        toast.error("Copy Error", {
          description: errorMessage,
          duration: 4000,
          action: {
            label: "Retry",
            onClick: () => copyTaskWithFeedback(todo),
          },
        });

        // Update ARIA live region
        updateAriaLive(`Error copying task "${todo.title}": ${errorMessage}`);

        // Reset button state after error display
        resetButtonState(taskId, 3000);

        return {
          success: false,
          taskTitle: todo.title,
          contentType: "basic",
          itemsCopied: {
            title: false,
            description: false,
            comments: 0,
            metadata: false,
          },
          formattedText: "",
          error: errorMessage,
        };
      }
    },
    [clearTimeoutForTask, updateButtonState, resetButtonState, updateAriaLive]
  );

  /**
   * Get CSS classes for button styling based on state
   */
  const getButtonClasses = useCallback(
    (taskId: string, baseClasses: string = ""): string => {
      const state = getButtonState(taskId);
      const stateClasses: string[] = [];

      // Thematic base: light = purple button w/ white text; dark = black w/ purple text
      const themeClasses = [
        "!bg-primary !text-white hover:!bg-primary/80 !border !border-primary/50",
        "dark:!bg-black dark:!text-primary/80 dark:!border-primary/50 dark:hover:!bg-black"
      ].join(" ");

      if (state.isLoading) {
        stateClasses.push("opacity-70 cursor-wait");
      } else if (state.isSuccess) {
        // No green; keep theme and add subtle ring to indicate success
        stateClasses.push("ring-2 ring-primary/40 dark:ring-primary");
      } else if (state.isError) {
        stateClasses.push("bg-red-500 text-white hover:bg-red-600 border-red-600 dark:bg-red-600 dark:text-white dark:border-red-700");
      }

      return `${baseClasses} ${themeClasses} ${stateClasses.join(" ")}`.trim();
    },
    [getButtonState]
  );

  /**
   * Create ARIA live region for screen readers
   * Should be rendered once in the app root
   */
  const createAriaLiveRegion = useCallback(() => {
    if (!ariaLiveRef.current && typeof document !== "undefined") {
      const liveRegion = document.createElement("div");
      liveRegion.setAttribute("aria-live", "polite");
      liveRegion.setAttribute("aria-atomic", "true");
      liveRegion.className = "sr-only";
      liveRegion.id = "copy-feedback-aria-live";
      document.body.appendChild(liveRegion);
      ariaLiveRef.current = liveRegion;
    }
    return ariaLiveRef.current;
  }, []);

  /**
   * Cleanup function to clear all timeouts
   */
  const cleanup = useCallback(() => {
    Object.values(timeoutRefs.current).forEach((timeout) => {
      if (timeout) {
        clearTimeout(timeout);
      }
    });
    timeoutRefs.current = {};
  }, []);

  return {
    copyTaskWithFeedback,
    getButtonState,
    getButtonClasses,
    createAriaLiveRegion,
    cleanup,
  };
};
